// @ts-nocheck
/**
 * Tiered Discount Preview Component
 *
 * Provides real-time discount preview on product pages and cart.
 * Shows customers how much they save in COP (not percentages) based
 * on the accumulated subtotal per product.
 *
 * Reads configuration from window.TieredDiscountConfig injected by theme.liquid
 */

const TIERED_DISCOUNT_DEFAULTS = {
  tiers: [
    { minSubtotal: 150000, maxSubtotal: 300000, percentage: 3 },
    { minSubtotal: 300000, maxSubtotal: 800000, percentage: 5 },
    { minSubtotal: 800000, maxSubtotal: 1200000, percentage: 8 },
  ],
  currency: 'COP',
  locale: 'es-CO',
};

/**
 * Gets the tiered discount configuration from the global scope
 * @returns {typeof TIERED_DISCOUNT_DEFAULTS}
 */
function getTieredConfig() {
  return window.TieredDiscountConfig || TIERED_DISCOUNT_DEFAULTS;
}

/**
 * Formats a number as COP currency
 * @param {number} value
 * @returns {string}
 */
function formatCurrency(value) {
  const config = getTieredConfig();
  return new Intl.NumberFormat(config.locale || 'es-CO', {
    style: 'currency',
    currency: config.currency || 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Finds the matching tier for a given subtotal
 * @param {number} subtotal
 * @returns {{ tier: object|null, nextTier: object|null, amountToNext: number }}
 */
function findMatchingTier(subtotal) {
  const config = getTieredConfig();
  const tiers = config.tiers || [];

  let matchedTier = null;
  let nextTier = null;

  for (let i = 0; i < tiers.length; i++) {
    if (subtotal >= tiers[i].minSubtotal && subtotal < tiers[i].maxSubtotal) {
      matchedTier = tiers[i];
      nextTier = tiers[i + 1] || null;
      break;
    }
    if (subtotal < tiers[i].minSubtotal && !matchedTier) {
      nextTier = tiers[i];
      break;
    }
  }

  const amountToNext = nextTier ? nextTier.minSubtotal - subtotal : 0;

  return { tier: matchedTier, nextTier, amountToNext };
}

// ─── Product Page Table Component ────────────────────────────────────────────

class TieredDiscountTable extends HTMLElement {
  connectedCallback() {
    this.productPrice = parseFloat(this.dataset.productPrice || '0');
    if (!this.productPrice) return;

    this.onQuantityChange = this.updateHighlight.bind(this);
    
    // Listen for any quantity input changes
    document.addEventListener('change', this.onQuantityChange);
    document.addEventListener('input', this.onQuantityChange);
    
    // Also listen to clicks near quantity buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('quantity-input') || e.target.closest('button')) {
        setTimeout(this.onQuantityChange, 50);
      }
    });

    // Fallback polling for tricky themes
    this._interval = setInterval(this.onQuantityChange, 500);

    // Initial highlight
    setTimeout(this.onQuantityChange, 100);
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onQuantityChange);
    document.removeEventListener('input', this.onQuantityChange);
    clearInterval(this._interval);
  }

  updateHighlight() {
    let quantityInput = document.querySelector('input[name="quantity"]');

    const quantity = parseInt(quantityInput?.value || '1');
    const subtotal = this.productPrice * quantity;

    const rows = this.querySelectorAll('.tiered-discount-table__row');
    rows.forEach((row) => {
      const min = parseFloat(row.dataset.min || '0');
      const max = parseFloat(row.dataset.max || '0');
      
      if (subtotal >= min && subtotal < max) {
        row.style.backgroundColor = '#e8f5e9'; // Bright green background
        row.style.borderLeft = '4px solid #2e7d32'; // Green border
        const price = row.querySelector('.tiered-discount-table__price');
        if (price) price.style.color = '#2e7d32';
      } else {
        row.style.backgroundColor = '';
        row.style.borderLeft = '';
        const price = row.querySelector('.tiered-discount-table__price');
        if (price) price.style.color = '';
      }
    });
  }
}

if (!customElements.get('tiered-discount-table')) {
  customElements.define('tiered-discount-table', TieredDiscountTable);
}

// ─── Cart Info Component ─────────────────────────────────────────────────────

class TieredDiscountCartInfo extends HTMLElement {
  #observer = null;

  connectedCallback() {
    this.productPrice = parseFloat(this.dataset.productPrice || '0');
    this.mayoristaPrice = parseFloat(this.dataset.mayoristaPrice || '0');
    if (this.mayoristaPrice <= 0) this.mayoristaPrice = this.productPrice;

    this.lineQuantity = parseInt(this.dataset.quantity || '0');
    this.render();

    // Observe quantity changes on the closest quantity input
    this.#observeQuantityChanges();
  }

  disconnectedCallback() {
    if (this.#observer) {
      this.#observer.disconnect();
    }
  }

  #observeQuantityChanges() {
    const row = this.closest('tr, .cart-item');
    if (!row) return;

    const quantityInput = row.querySelector('input[type="number"]');
    if (!quantityInput) return;

    // Listen for input changes
    quantityInput.addEventListener('change', () => {
      this.lineQuantity = parseInt(quantityInput.value) || 0;
      this.render();
    });
  }

  render() {
    if (!this.productPrice || this.productPrice <= 0 || !this.lineQuantity) {
      this.innerHTML = '';
      return;
    }

    const subtotal = this.productPrice * this.lineQuantity;
    const { tier, nextTier, amountToNext } = findMatchingTier(subtotal);

    let html = '';
    let activeTierMsg = '';
    if (subtotal >= 800000) {
      activeTierMsg = 'Descuento de $800.000 en adelante activado';
    } else if (subtotal >= 300000) {
      activeTierMsg = 'Descuento de $300.000 a $800.000 activado';
    } else if (subtotal >= 150000) {
      activeTierMsg = 'Descuento de $150.000 a $300.000 activado';
    } else {
      activeTierMsg = 'Precio Mayorista (De $0 a $150.000) activado';
    }

    if (tier || subtotal < 150000) {
      // Use the mayorista price passed from Liquid
      let basePrice = this.mayoristaPrice || this.productPrice;

      const activePercentage = tier ? tier.percentage : 0;
      const rawDiscountPerUnit = (basePrice * activePercentage) / 100;
      const roundedTargetPrice = Math.round((basePrice - rawDiscountPerUnit) / 5) * 5;
      
      const unitSavings = this.productPrice - roundedTargetPrice;
      const totalSavings = unitSavings * this.lineQuantity;

      html = `
        <div class="tiered-discount-cart-info tiered-discount-cart-info--active">
          <span class="tiered-discount-cart-info__badge" style="font-weight: 500;">
            🏷️ ${activeTierMsg}
          </span>
          <span class="tiered-discount-cart-info__savings">
            · Unidad: <strong>${formatCurrency(roundedTargetPrice)}</strong>
          </span>
        </div>
      `;
    }

    if (nextTier) {
      html += `
        <div class="tiered-discount-cart-info tiered-discount-cart-info--next">
          <span class="tiered-discount-cart-info__hint">
            ✨ ¡Agrega ${formatCurrency(Math.round(amountToNext))} más para ahorrar más!
          </span>
        </div>
      `;
    }

    this.innerHTML = html;
  }
}

if (!customElements.get('tiered-discount-cart-info')) {
  customElements.define('tiered-discount-cart-info', TieredDiscountCartInfo);
}

// ─── Product Page Dynamic Preview ────────────────────────────────────────────

class TieredDiscountPreview extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    this.productPrice = parseFloat(this.dataset.productPrice || '0');
    this.productId = this.dataset.productId || '';

    const { signal } = this.#abortController;

    // Listen for quantity changes to update preview
    document.addEventListener(
      'theme:quantity-selector:update',
      this.#onQuantityChange.bind(this),
      { signal }
    );

    this.#updatePreview();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #onQuantityChange(event) {
    // Check if this event is for our product form
    const form = this.closest('product-form-component');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;

    this.#updatePreview();
  }

  #updatePreview() {
    if (!this.productPrice) return;

    // Get current quantity from the closest quantity input
    const form = this.closest('product-form-component') || this.closest('form');
    const quantityInput = form?.querySelector('input[name="quantity"]');
    const quantity = parseInt(quantityInput?.value || '1');

    // Get cart quantity for this product
    const cartQty = parseInt(quantityInput?.getAttribute('data-cart-quantity') || '0');
    const totalQty = quantity + cartQty;

    const subtotal = this.productPrice * totalQty;
    const { tier, nextTier, amountToNext } = findMatchingTier(subtotal);

    // Highlight the active row in the table
    const table = this.closest('.product-form-buttons')?.previousElementSibling ||
                  document.querySelector('tiered-discount-table');
    if (table) {
      const rows = table.querySelectorAll('.tiered-discount-table__row');
      rows.forEach((row) => {
        const min = parseFloat(row.dataset.min || '0');
        const max = parseFloat(row.dataset.max || '0');
        if (subtotal >= min && subtotal < max) {
          row.classList.add('tiered-discount-table__row--active');
        } else {
          row.classList.remove('tiered-discount-table__row--active');
        }
      });
    }

    // Update the preview badge
    const badge = this.querySelector('.tiered-discount-preview__badge');
    if (badge) {
      if (tier) {
        const rawDiscountedPrice = this.productPrice - (this.productPrice * tier.percentage) / 100;
        const roundedDiscountedPrice = Math.round(rawDiscountedPrice / 5) * 5;
        const savings = this.productPrice - roundedDiscountedPrice;
        badge.innerHTML = `🏷️ ¡Ahorras ${formatCurrency(savings)}/ud con este pedido!`;
        badge.classList.add('tiered-discount-preview__badge--active');
      } else if (nextTier) {
        badge.innerHTML = `✨ ¡Agrega ${formatCurrency(Math.round(amountToNext))} más para obtener descuento!`;
        badge.classList.remove('tiered-discount-preview__badge--active');
      } else {
        badge.innerHTML = '';
      }
    }
  }
}

if (!customElements.get('tiered-discount-preview')) {
  customElements.define('tiered-discount-preview', TieredDiscountPreview);
}
